/**
 * The consolidated executive dashboard (KAN-42 / KAN-60 / KAN-96 / KAN-97).
 *
 * This module is a *contract*, not a calculation. That distinction is the whole
 * design, and it comes straight out of KAN-96's central rule: the executive
 * dashboard must never be a second implementation of a metric. Leadership is
 * the audience least able to check a figure and most likely to quote it, so a
 * number here that disagrees with the module it came from does not read as a
 * bug in one screen — it discredits every screen.
 *
 * So nothing below computes money. What lives here is the part both sides have
 * to agree on:
 *
 *  - **which** metrics exist, in the order they matter;
 *  - **where** each one comes from and which page it drills into;
 *  - **which way is good** for each one, because that is not universal —
 *    revenue over budget is favourable and expenses over budget are not, and a
 *    dashboard that colours both red is worse than one with no colour at all;
 *  - **what "no number" means**, of which there are several genuinely different
 *    cases that must not collapse into a blank.
 *
 * The API reads this to build a snapshot; the SPA reads it to render one. Adding
 * a metric without saying where it drills to, which way is favourable, or what
 * grant it needs is a type error rather than an omission somebody notices in a
 * board meeting.
 */
/* ------------------------------------------------------------------------ *
 * The metrics
 * ------------------------------------------------------------------------ */
/**
 * The nine KPI groups KAN-96 names, **in order of importance**.
 *
 * The order is load-bearing rather than cosmetic: KAN-97 requires the most
 * important tiles above the fold, and "above the fold" is a decision about
 * which figures a person who looks at this for thirty seconds needs first. It
 * is declared here rather than in the layout so the API and the SPA cannot
 * disagree about it, and so re-prioritising is one edit.
 *
 * Cash first because it is the only figure that can end a firm. Then what is
 * owed to it, then what it has done but not yet billed, then client money, then
 * what it owes. The forecasts and the budget comparison come last: they are
 * planning figures, and nobody makes a same-day decision on them.
 */
export const EXECUTIVE_METRICS = [
    'cash_position',
    'ar',
    'wip',
    'trust_transfers',
    'ap',
    'billings_vs_target',
    'cash_flow_forecast',
    'payroll_forecast',
    'budget_vs_actual',
];
export const EXECUTIVE_METRIC_META = {
    cash_position: {
        label: 'Cash position',
        description: 'Cleared balance across the firm operating accounts.',
        unit: 'money',
        polarity: 'higher_is_better',
        permission: 'cash_flow.view',
        drillTo: null,
        sourceTicket: 'KAN-49',
    },
    ar: {
        label: 'Accounts receivable',
        description: 'Invoiced and unpaid, across every client and matter.',
        unit: 'money',
        polarity: 'lower_is_better',
        permission: 'ar.view',
        drillTo: '/accounts-receivable',
        sourceTicket: 'KAN-87',
    },
    wip: {
        label: 'Work in progress',
        description: 'Billable time recorded, not yet invoiced.',
        unit: 'money',
        polarity: 'higher_is_better',
        permission: 'firm_financials.view',
        drillTo: '/billing-forecast',
        sourceTicket: 'KAN-91',
    },
    trust_transfers: {
        label: 'Projected trust transfers',
        description: 'Client money already held that the next bill can draw on.',
        unit: 'money',
        /**
         * Neutral, and deliberately so. A larger projected transfer means more of
         * the coming bill is already funded, which is good for the firm — but the
         * underlying figure is client money held in IOLTA, and a tile that rewards
         * its own growth is the wrong instinct to encourage on a trust balance.
         */
        polarity: 'neutral',
        permission: 'firm_financials.view',
        drillTo: '/billing-forecast',
        sourceTicket: 'KAN-85',
    },
    ap: {
        label: 'Accounts payable',
        description: 'Vendor bills received and unpaid.',
        unit: 'money',
        polarity: 'lower_is_better',
        permission: 'firm_financials.view',
        drillTo: null,
        sourceTicket: 'KAN-68',
    },
    billings_vs_target: {
        label: 'Billings vs. target',
        description: 'Billed this period against the production target.',
        unit: 'bps',
        polarity: 'higher_is_better',
        permission: 'firm_financials.view',
        drillTo: null,
        sourceTicket: 'KAN-76',
    },
    cash_flow_forecast: {
        label: 'Cash flow forecast',
        description: 'Projected cash 30 days out, after known obligations.',
        unit: 'money',
        polarity: 'higher_is_better',
        permission: 'cash_flow.view',
        drillTo: null,
        sourceTicket: 'KAN-90',
    },
    payroll_forecast: {
        label: 'Payroll & obligations',
        description: 'Scheduled payroll, tax and recurring outflows ahead.',
        unit: 'money',
        /**
         * Neutral. A payroll figure is a commitment, not a result — it rises
         * because the firm hired somebody. Colouring an increase red would read as
         * a warning about a decision leadership already took on purpose.
         */
        polarity: 'neutral',
        permission: 'cash_flow.view',
        drillTo: null,
        sourceTicket: 'KAN-89',
    },
    budget_vs_actual: {
        label: 'Budget vs. actual',
        description: 'Spend against budget for the current period.',
        unit: 'bps',
        /**
         * Lower is better *because this tile is scoped to spend*. The general
         * "over budget can be good" case KAN-97 warns about is real — it applies to
         * revenue lines — and the way this codebase avoids getting it wrong is to
         * keep the two as separate metrics with separate polarity rather than one
         * signed variance whose meaning depends on the row.
         */
        polarity: 'lower_is_better',
        permission: 'firm_financials.view',
        drillTo: null,
        sourceTicket: 'KAN-93',
    },
};
/**
 * How many tiles the layout treats as "above the fold".
 *
 * Five, which is what fits on a phone in one screen without shrinking the
 * figures past reading size — and leadership opening this on a phone is a
 * stated requirement rather than an edge case.
 */
export const ABOVE_THE_FOLD = 5;
/* ------------------------------------------------------------------------ *
 * The comparison window
 * ------------------------------------------------------------------------ */
/**
 * The date range selector (KAN-97).
 *
 * It selects the **comparison baseline**, not the current value. That is worth
 * being precise about, because the obvious reading — that the range filters the
 * figures — is not something most of these metrics can honour: AR outstanding
 * and trust held are positions as at now, not sums over a window, and there is
 * no such thing as "AR for last quarter" without the invoice balances of last
 * quarter.
 *
 * Applying it to the baseline instead gives the selector one consistent meaning
 * across every tile, which is what the acceptance criterion actually asks for:
 * every figure is current, and every figure is compared against the same point
 * in the past.
 */
export const EXECUTIVE_RANGES = ['week', 'month', 'quarter', 'year'];
export const DEFAULT_EXECUTIVE_RANGE = 'month';
export const EXECUTIVE_RANGE_META = {
    week: { label: '7 days', days: 7, comparisonLabel: 'vs. a week ago' },
    month: { label: '30 days', days: 30, comparisonLabel: 'vs. a month ago' },
    quarter: { label: '90 days', days: 90, comparisonLabel: 'vs. a quarter ago' },
    year: { label: '12 months', days: 365, comparisonLabel: 'vs. a year ago' },
};
export function directionOf(changeMinor) {
    if (changeMinor > 0)
        return 'up';
    if (changeMinor < 0)
        return 'down';
    return 'flat';
}
export function favourabilityOf(key, changeMinor) {
    const { polarity } = EXECUTIVE_METRIC_META[key];
    if (polarity === 'neutral' || changeMinor === 0)
        return 'neutral';
    const rising = changeMinor > 0;
    return (polarity === 'higher_is_better') === rising ? 'favourable' : 'unfavourable';
}
/**
 * The words that go with the arrow.
 *
 * KAN-97 requires that favourable/unfavourable never be conveyed by colour
 * alone — for accessibility, and because these screens get printed and pasted
 * into board packs in greyscale. Providing the text here rather than in the
 * component is what stops a future tile shipping with the arrow and no label.
 */
export const DIRECTION_LABELS = {
    up: 'up',
    down: 'down',
    flat: 'unchanged',
};
export const FAVOURABILITY_LABELS = {
    favourable: 'favourable',
    unfavourable: 'unfavourable',
    neutral: 'no judgement',
};
/**
 * The tolerance for the comparison: zero.
 *
 * Both sides are integer minor units produced by the same function, so there is
 * nothing for a tolerance to absorb — and KAN-96's definition of done says any
 * divergence, however small, is a defect rather than a rounding footnote. A cent
 * of slack here would be a cent nobody could account for, and would hide the
 * only failure this check is built to catch.
 */
export const EXECUTIVE_CONSISTENCY_TOLERANCE_MINOR = 0;
//# sourceMappingURL=executive.js.map